import type { CheckinPlace } from '@awwyeah/shared';

/**
 * The place visual: **a drawing, not a map.**
 *
 * Two crossed rules and a coloured dot, rendered from nothing but CSS. There is no tile
 * request, no geocode, no map SDK and no third-party anything — north-star #7 ("no
 * external geo/tile/API calls") and the design's location rule both land here. The place
 * *name* is whatever the member typed; the coordinates, if they granted permission, are
 * carried in the doc for a future self-hosted map and are never sent anywhere today.
 */

export function PlacePin({
  color,
  size = 44,
}: {
  /** The check-in author's own profile hex. */
  color: string;
  size?: 40 | 44 | 64;
}): JSX.Element {
  const box = size === 64 ? 'h-16 w-16' : size === 40 ? 'h-10 w-10' : 'h-11 w-11';
  return (
    <span className={`relative flex-shrink-0 overflow-hidden bg-raised ${box}`} aria-hidden="true">
      <span className="absolute inset-x-0 top-[36%] h-0.5 bg-line" />
      <span className="absolute inset-y-0 left-[42%] w-0.5 bg-line" />
      <span className="absolute inset-x-0 bottom-[28%] h-px bg-line" />
      <span
        style={{ backgroundColor: color }}
        className="absolute left-[34%] top-[26%] h-2 w-2 rounded-full border-2 border-surface"
      />
    </span>
  );
}

/** The place row that sits under a photo (or alone on a text-only post). */
export function PlaceRow({ place, color }: { place: CheckinPlace; color: string }): JSX.Element {
  return (
    <div
      className="flex items-center gap-2.5 border border-line bg-bg px-2.5 py-2"
      data-testid="checkin-place"
    >
      <PlacePin color={color} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-fg">{place.name}</div>
        <div className="text-xs text-fg-3">
          {place.lat !== undefined && place.lng !== undefined
            ? 'Pinned where you were, once'
            : 'Named by hand'}
        </div>
      </div>
    </div>
  );
}
